/**
 * Places, geocoding and route geometry — asked of OUR API, not of Google.
 *
 * The browser used to make these calls through the Maps JS SDK, which meant the
 * key in the bundle had to have Places, Geocoding and Directions enabled. It
 * ships to every visitor, so that key was effectively a public licence to spend
 * our Google quota — and the same key the servers used (SEC-1).
 *
 * trips-server now makes those calls with the key from Secrets Manager. What is
 * left in the bundle is a key restricted to the Maps JavaScript API alone: it
 * can draw a map and nothing else.
 */

import { getTokensFromCache } from '../auth/utils/tokenCache';

const API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

export interface ServerPlacePhoto {
  name: string;
  /** Resolved server-side: a plain CDN link, carrying no key of ours. */
  url?: string;
}

export interface ServerPlace {
  placeId: string;
  name: string;
  formattedAddress?: string;
  rating?: number;
  userRatingCount?: number;
  openingHours?: string[];
  openNow?: boolean;
  photos?: ServerPlacePhoto[];
  location?: { lat: number; lng: number };
  types?: string[];
  website?: string;
  phone?: string;
  mapsUri?: string;
  priceLabel?: string;
  businessStatus?: string;
  summary?: string;
}

/**
 * Every lookup here is decoration on a map: a card, a pin, a drawn route. None
 * of it is worth an error boundary, so failures resolve to null/[] and the
 * caller renders what it already knows — exactly how the SDK calls behaved.
 */
const get = async <T,>(path: string): Promise<T | null> => {
  if (!API_ENDPOINT) return null;
  const token = getTokensFromCache()?.accessToken;
  if (!token) return null;

  try {
    const response = await fetch(`${API_ENDPOINT}${path}`, {
      method: 'GET',
      // No Bearer prefix — API Gateway's Cognito authorizer wants the raw token.
      headers: { Authorization: token },
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
};

export const fetchPlaceFromServer = async (
  placeId?: string,
  query?: string,
): Promise<ServerPlace | null> => {
  const params = new URLSearchParams();
  if (placeId) params.set('placeId', placeId);
  if (query?.trim()) params.set('query', query.trim());
  if (![...params.keys()].length) return null;

  const data = await get<{ place: ServerPlace }>(`/places/details?${params.toString()}`);
  return data?.place || null;
};

export const searchPlacesOnServer = async (
  query: string,
  options: { limit?: number; bias?: { lat: number; lng: number } } = {},
): Promise<ServerPlace[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const params = new URLSearchParams({ q: trimmed });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.bias) {
    params.set('lat', String(options.bias.lat));
    params.set('lng', String(options.bias.lng));
  }

  const data = await get<{ places: ServerPlace[] }>(`/places/search?${params.toString()}`);
  return data?.places || [];
};

export const geocodeOnServer = async (
  address: string,
): Promise<{ lat: number; lng: number } | null> => {
  const trimmed = address.trim();
  if (!trimmed) return null;
  const data = await get<{ location: { lat: number; lng: number } }>(
    `/places/geocode?address=${encodeURIComponent(trimmed)}`,
  );
  return data?.location || null;
};

/**
 * The road geometry between two points, as Google's ENCODED polyline.
 *
 * The caller decodes it with the Maps geometry library, which the map already
 * loads — so this stays small on the wire and needs no Directions access in the
 * browser. A null polyline means "draw the straight line", which is the same
 * thing an unroutable leg has always meant.
 */
export const fetchPathFromServer = async (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
  mode: string,
): Promise<string | null> => {
  const params = new URLSearchParams({
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    mode,
  });
  const data = await get<{ path: { polyline: string | null } }>(`/places/path?${params.toString()}`);
  return data?.path?.polyline || null;
};
